import { trackLearningAction } from 'analytics';
import forge from 'apis/forge';
import SidebarToggle from 'components/Widget/Button/SidebarToggle';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { CreateSceneFromPromptResponse, CreateSceneResponse } from 'dragonmouse-shared/api/forge';
import { Attachment } from 'dragonmouse-shared/interfaces';
import { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectIsMobile } from 'redux/mode/reducers';
import * as routes from 'routes';
import CreatorInput from '../CreatorInput';
import * as Base from '../styles';
import { RootReducer } from 'redux/reducers';

const ProjectCreator = () => {
	const navigate = useNavigate();
	// ProjectCreator has its own loader. Like the projects page, it does some
	// things without optimistic updates. When it does this, it shows a semi-transparent loading
	// which is different from the main App's loading indicator (which is opaque).
	// [perhaps we should generalize the main app's loader - ss]
	//
	// We use the existence of a loading message to gate the display of the loader
	const [loadingMessage, setLoadingMessage] = useState<string>('');
	const [selectedDesignSystemId, setSelectedDesignSystemId] = useState<string | null>(null);

	const { isMobile } = useSelector(
		(r: RootReducer) => ({
			isMobile: selectIsMobile(r.mode),
		}),
		shallowEqual
	);
	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onNewProject = async (prompt: string, attachments: Attachment[]) => {
		try {
			setLoadingMessage('creating project...');
			trackLearningAction('onboarding_prompt_input', 'complete');
			const res: CreateSceneFromPromptResponse = await forge.createSceneFromPrompt.withToast()({
				prompt,
				attachments,
				...(selectedDesignSystemId && { designSystemId: selectedDesignSystemId }),
			});
			setLoadingMessage('');
			navigate(routes.scene(res.scene.id, res.scene.title));
		} catch (e) {
			setLoadingMessage('');
		}
	};

	const onDesignSystemSelect = (designSystemId: string | null) => {
		setSelectedDesignSystemId(designSystemId);
	};

	const onCreateEmptyProject = async () => {
		try {
			setLoadingMessage('creating project...');
			const res: CreateSceneResponse = await forge.createScene.withToast()({
				title: 'My Project',
				...(selectedDesignSystemId && { designSystemId: selectedDesignSystemId }),
			});
			setLoadingMessage('');
			navigate(routes.scene(res.sceneId, 'My Project'));
		} catch (e) {
			setLoadingMessage('');
		}
	};


	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	return (
		<Base.Base>
			{isMobile && (
				<Base.SidebarToggle>
					<SidebarToggle />
				</Base.SidebarToggle>
			)}
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<Base.Content>
				<Base.Input>
					<Base.Title>What would you like to design&nbsp;today?</Base.Title>
					<CreatorInput
						onSubmit={onNewProject}
						disabled={!!loadingMessage}
						mode="project"
						onDesignSystemSelect={onDesignSystemSelect}
						onCreateBlank={onCreateEmptyProject}
					/>
				</Base.Input>
			</Base.Content>
		</Base.Base>
	);
};

export default ProjectCreator;
